// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { HistoryType } from "../../Utils/History/HistoryType.ts"
import { ErrorInformationStorage } from "../ErrorInformationStorage.js"
import { CustomEvent } from "../../Utils/CustomEvents.js"
import { InstrumentSpecificType } from "../../Utils/Instruments/InstrumentSpecificType.js"
import { QuoteSubscriptionItem } from "./QuoteSubscriptionItem.ts"

export let QuoteCache = function (dataCache)
{
    this.DataCache = dataCache
    this.subsDict = {}
    this.onRequestQuotesSubscription = new CustomEvent()
    this.onCancelQuotesSubscription = new CustomEvent()
    this.serverTimeZone = 0
    this.onReloadHistory = null;
    this.SubsRoutesMap = new Object();
}

/**
    * Вызов делегата загрузки истории
    * 
    * @param { import('./Instrument.js').Instrument} instrument
    * @param {ReloadHistoryParams} historyParams
    * @param {AbortSignal} signal
*/
QuoteCache.prototype.callOnReloadHistory = async function (historyParams, signal)
{
    if (!this.onReloadHistory)
    {
        return [];
    }

    const intervals = await this.onReloadHistory(this, historyParams, signal);
    return intervals;
}

QuoteCache.prototype.newQuoteMessage = function (msg)
{
    let subsItem = null;

    subsItem = this.getSubscriptionItemByMsg(msg);

    let SubsItems = this.SubsRoutesMap[msg.Route]
    if (SubsItems)
    {
        let Items = SubsItems.GetItems();
        for (let i = 0, len = Items.length; i < len; i++)
        {
            msg.Route = Items[i];
            this.newQuoteMessage(msg);
        }
    }

    if (!subsItem)
        return;

    if (subsItem.HasContiniousContractItem)
    {
        msg.TargetInstrumentName = subsItem.InstrumentContiniousContractItem.instrument.GetInteriorID();
        subsItem.InstrumentContiniousContractItem.newQuote(msg);
    }
    msg.TargetInstrumentName = subsItem.instrument.GetInteriorID();
    subsItem.newQuote(msg)
}

QuoteCache.prototype.addListener = function (instrument, listenerObject, quoteType, isDataSource)
{
    if (!this.DataCache.Loaded || instrument.IsEmpty)
        return;

    let subsRoute = this.DataCache.getRouteByName(instrument.Route);

    if (subsRoute && subsRoute.QuoteRouteId !== subsRoute.RouteId)
    {
        let quoteSubsRoute = this.DataCache.getRouteById(subsRoute.QuoteRouteId);
        if (quoteSubsRoute)
        {
            if (instrument.RoutesSupportedArray.indexOf(quoteSubsRoute.RouteId) === -1)
                return

            let ItemSubs = this.SubsRoutesMap[quoteSubsRoute.RouteId];
            if (!ItemSubs)
            {
                ItemSubs = new SubscriptionMapItem(quoteSubsRoute.RouteId)
                this.SubsRoutesMap[quoteSubsRoute.RouteId] = ItemSubs;
            }
            ItemSubs.Add(subsRoute.RouteId);
        }
    }

    if (subsRoute && !subsRoute.CanSubscribe())
        return;

    let delayQuotes = [];
    if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract)
    {
        let subIns = this.DataCache.getInstrument(instrument.InstrumentTradableID.toString(), instrument.Route);
        if (subIns)
        {
            this.addListener(subIns, listenerObject, quoteType);

            let subInsSubsItem = this.getSubscriptionItem(subIns)
            // if subIns already subscribed. Send all quotes 
            if (subInsSubsItem && subInsSubsItem.lastQuoteDict)
                for (let q in subInsSubsItem.lastQuoteDict)
                    delayQuotes.push(subInsSubsItem.lastQuoteDict[q])
            delayQuotes = delayQuotes.concat(subIns.GeneratedInstrumentDayBarMessages())
        }
    }

    let subsItem = this.getSubscriptionItem(instrument)
    let listenersArray = subsItem.getListenerArray(quoteType);

    for (let q of delayQuotes)
        subsItem.newQuote(q);

    let isSubscribed = listenersArray.indexOf(listenerObject) >= 0;

    let needSubscribeMsg = listenersArray.length === 0 && (quoteType != HistoryType.QUOTE_INSTRUMENT_DAY_BAR || isDataSource);

    if (!isSubscribed)
        subsItem.addListener(listenerObject, quoteType);

    if (needSubscribeMsg)
        this.onRequestQuotesSubscription.Raise(instrument.InstrumentTradableID, instrument.Route, quoteType, false, isDataSource || false)

    if (quoteType != HistoryType.QUOTE_INSTRUMENT_DAY_BAR && !isDataSource)                                 // подписка на IDBM сопровождает любую другую подписку (кроме подписки такого же типа или из дата сорс)
        this.addListener(instrument, listenerObject, HistoryType.QUOTE_INSTRUMENT_DAY_BAR, isDataSource)    // потому даже если меседж на подписку не нужен (его успел раньше отправить DataSource), надо все равно зарегистрировать слушателя как подписанного на IDBM (иначе при закрытии дата сорса произойдет отписка от IDBM)
}

QuoteCache.prototype.removeListener = function (instrument, listenerObject, quoteType, isDataSource)
{
    if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract)
    {
        let subIns = this.DataCache.getInstrument(instrument.InstrumentTradableID.toString(), instrument.Route);
        if (subIns)
            this.removeListener(subIns, listenerObject, quoteType);

        let subsItem = this.getSubscriptionItem(instrument, false);
        if (!subsItem)
        {
            return;
        }
        let oldListenersCount = subsItem.getListenerCount(quoteType)

        subsItem.removeListener(listenerObject, quoteType)

        let newListenersCount = subsItem.getListenerCount(quoteType)
        let wasRemovedListener = oldListenersCount != newListenersCount
        if (quoteType != HistoryType.QUOTE_INSTRUMENT_DAY_BAR && !isDataSource && wasRemovedListener)
            this.removeListener(instrument, listenerObject, HistoryType.QUOTE_INSTRUMENT_DAY_BAR, isDataSource)

        return
    }

    let subsItem = this.getSubscriptionItem(instrument, false);
    if (!subsItem)
    {
        return;
    }
    let unsubscribe = true;
    let oldListenersCount = subsItem.getListenerCount(quoteType)

    subsItem.removeListener(listenerObject, quoteType)

    let newListenersCount = subsItem.getListenerCount(quoteType)
    let wasRemovedListener = oldListenersCount != newListenersCount

    unsubscribe = wasRemovedListener && newListenersCount === 0 && (quoteType != HistoryType.QUOTE_INSTRUMENT_DAY_BAR || isDataSource);

    if (unsubscribe)
        this.onCancelQuotesSubscription.Raise(instrument.InstrumentTradableID, instrument.Route, quoteType, false, isDataSource || false)

    if (quoteType != HistoryType.QUOTE_INSTRUMENT_DAY_BAR && !isDataSource && wasRemovedListener)
        this.removeListener(instrument, listenerObject, HistoryType.QUOTE_INSTRUMENT_DAY_BAR, isDataSource)
}

QuoteCache.prototype.reSubscribe = function (subscribe, instrumentList)
{
    if (!instrumentList.length)
        return;

    let allSubsItems = this.getAllSubscriptionItemArray();

    for (let i = 0; i < allSubsItems.length; i++)
    {

        let si = allSubsItems[i];
        let ins = si.instrument;
        if (instrumentList.indexOf(ins) === -1)
            continue;

        let ld = si.listenerDict;

        for (let l in ld)
        {
            let arr = ld[l];
            if (!arr.length)
                continue;

            if (subscribe)
                this.onRequestQuotesSubscription.Raise(ins.InstrumentTradableID, ins.Route, parseInt(l), true)
            else
                this.onCancelQuotesSubscription.Raise(ins.InstrumentTradableID, ins.Route, parseInt(l), true)
        }
    }
}

QuoteCache.prototype.getAllSubscriptionItemArray = function ()
{
    let result = [];
    for (let sdi in this.subsDict)
        for (let qsi in this.subsDict[sdi])
            result.push(this.subsDict[sdi][qsi])
    return result;
}

// TODO. Rename. Creates subs item as well if doesn't exist.
QuoteCache.prototype.getSubscriptionItem = function (instrument, createSubItem = true)
{
    if (!instrument) return null

    //идентификаторы 
    let route = instrument.Route;
    let tradableId = instrument.InstrumentTradableID;

    let subsItem = null;
    let routeItem = this.subsDict[route];

    //let routeObj = DataCache.getRouteByName(route);
    //if (!routeObj)
    //    routeObj = DataCache.getInfoRouteByName(route);
    //if (routeObj && routeObj.QuoteRouteId !== route)
    //    route = routeObj.QuoteRouteId;

    let isContinious = instrument.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract
    //subsDict структура object[route] => object[tradableId]  =>  item
    if (!routeItem)
    {
        routeItem = new Object();
        this.subsDict[route] = routeItem;
    }

    subsItem = routeItem[tradableId];

    if (!subsItem && createSubItem)
    {
        subsItem = new QuoteSubscriptionItem(instrument, this)
        this.subsDict[route][tradableId] = subsItem;
    }

    if (!subsItem)
    {
        return null;
    }

    if (isContinious)
    {
        if (!subsItem.HasContiniousContractItem)
        {
            subsItem.InstrumentContiniousContractItem = new QuoteSubscriptionItem(instrument, this);
            subsItem.HasContiniousContractItem = true;
        }
        subsItem = subsItem.InstrumentContiniousContractItem;
    }

    return subsItem
}

QuoteCache.prototype.getSubscriptionItemByMsg = function (msg)
{
    if (!msg)
        return null

    let routeItem = this.subsDict[msg.Route];
    if (!routeItem)
        return null;

    let subsItem = routeItem[msg.InstrumentTradableID]
    if (!subsItem)
        return null;

    return subsItem;
}

QuoteCache.prototype.resubscribe = function ()
{
    // TODO.
}

QuoteCache.prototype.clear = function ()
{
    let subsDict = this.subsDict
    for (let key in subsDict)
    {
        let subsItem = subsDict[key]
        for (let s in subsItem)
            subsItem[s].dispose()
    }
    this.subsDict = {};
    this.SubsRoutesMap = {};
}

QuoteCache.prototype.GetLevel2Cash = function (instrument)
{
    let subsItem = this.getSubscriptionItem(instrument);
    if (subsItem)
        return subsItem.lvl2CashItem;

    return null;
}

var SubscriptionMapItem = function (RouteId)
{
    this.RouteId = RouteId;
    this.RouteIdMap = {};
    this.RouteIdArray = [];
}

SubscriptionMapItem.prototype.Add = function (routeId)
{
    if (this.RouteIdMap[routeId])
        return;

    this.RouteIdMap[routeId] = true;
    this.RouteIdArray.push(routeId);
}


SubscriptionMapItem.prototype.GetItems = function (routeId)
{
    return this.RouteIdArray;
}